// src/components/CapstoneProject.jsx
import React, { useState } from 'react';
import { FaBook, FaMicroscope, FaChartBar, FaQuoteRight, FaChalkboardTeacher } from 'react-icons/fa';

const CapstoneProject = ({ user }) => {
  const [step, setStep] = useState(1);
  const [citationStyle, setCitationStyle] = useState('APA');

  // Stages based on your academic requirements
  const steps = [
    { id: 1, title: 'Proposal & Intro', icon: <FaBook /> },
    { id: 2, title: 'Lit Review & Methods', icon: <FaMicroscope /> },
    { id: 3, title: 'Results & Analysis', icon: <FaChartBar /> },
    { id: 4, title: 'Citations (APA/MLA)', icon: <FaQuoteRight /> },
    { id: 5, title: 'Presentation & Defense', icon: <FaChalkboardTeacher /> },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 min-h-[700px] flex flex-col">
      
      {/* --- HEADER --- */}
      <div className="bg-indigo-900 text-white p-6 rounded-t-xl">
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <span className="text-3xl">🎓</span> Capstone Project Architect
        </h2>
        <p className="text-indigo-200 text-sm mt-1">
          Professional English Course • Research & Problem Solving Module
        </p>
        
        {/* Progress Stepper */}
        <div className="flex justify-between mt-8 px-4 relative">
          {steps.map((s) => (
            <div 
              key={s.id} 
              onClick={() => setStep(s.id)}
              className={`flex flex-col items-center z-10 cursor-pointer transition-all ${step === s.id ? 'opacity-100 scale-110' : 'opacity-60 hover:opacity-100'}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 ${step === s.id ? 'bg-yellow-400 border-yellow-400 text-indigo-900' : 'bg-indigo-800 border-indigo-400 text-white'}`}>
                {s.icon}
              </div>
              <span className="text-[10px] uppercase mt-2 font-bold tracking-wider">{s.title}</span>
            </div>
          ))}
          {/* Progress Line */}
          <div className="absolute top-5 left-0 w-full h-0.5 bg-indigo-700 -z-0" />
        </div>
      </div>

      {/* --- CONTENT BODY --- */}
      <div className="p-8 flex-1 overflow-y-auto">

        {/* STEP 1: INTRODUCTION */}
        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-blue-50 p-4 border-l-4 border-blue-500 rounded">
              <h4 className="font-bold text-blue-900">Phase 1: Defining the Problem</h4>
              <p className="text-sm text-blue-800">Identify a specific professional issue (e.g., "Reducing Last-Mile Delivery Times" or "Client Communication in Aesthetics").</p>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Problem Statement / Research Question</label>
              <textarea className="w-full border p-3 rounded focus:ring-2 focus:ring-indigo-500 h-32" placeholder="Clearly outline the specific professional issue being addressed..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Project Management Plan (Documentation)</label>
              <textarea className="w-full border p-3 rounded focus:ring-2 focus:ring-indigo-500 h-24" placeholder="Outline your timeline, stakeholders, and communication log strategy..." />
            </div>
          </div>
        )}

        {/* STEP 2: LIT REVIEW & METHODOLOGY */}
        {step === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Literature Review</h4>
                <p className="text-xs text-gray-500 mb-2">Summarize existing theories, regulations (e.g., Aviation Safety Protocols), and best practices.</p>
                <textarea className="w-full border p-3 rounded h-64" placeholder="Analyze existing information..." />
              </div>
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Methodology</h4>
                <p className="text-xs text-gray-500 mb-2">How will you gather data? (Interviews, Surveys, Case Studies, Industry Models like CAMEL).</p>
                <textarea className="w-full border p-3 rounded h-64" placeholder="Describe your investigation methods..." />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: RESULTS & ANALYSIS */}
        {step === 3 && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-green-50 p-4 border-l-4 border-green-500 rounded flex justify-between items-center">
              <div>
                <h4 className="font-bold text-green-900">Data Visualization</h4>
                <p className="text-sm text-green-800">Professional English includes presenting data clearly in tables or charts.</p>
              </div>
              <button className="bg-white text-green-700 px-3 py-1 rounded shadow text-xs font-bold">Upload Charts</button>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Results & Analysis</label>
              <textarea className="w-full border p-3 rounded h-32" placeholder="Present your findings..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Discussion & Limitations</label>
              <textarea className="w-full border p-3 rounded h-32" placeholder="Interpret results. What are the implications? What were the limitations?" />
            </div>
          </div>
        )}

        {/* STEP 4: CITATIONS & FORMATTING */}
        {step === 4 && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">References & Formatting</h3>
              <select 
                value={citationStyle} 
                onChange={(e) => setCitationStyle(e.target.value)}
                className="border-2 border-indigo-200 rounded px-3 py-1 text-indigo-700 font-bold"
              >
                <option value="APA">APA Style</option>
                <option value="MLA">MLA Style</option>
                <option value="Turabian">Turabian Style</option>
              </select>
            </div>

            <div className="bg-gray-50 p-6 rounded border border-gray-200">
              <h4 className="font-bold text-gray-700 mb-2">Literature List ({citationStyle})</h4>
              <p className="text-xs text-gray-500 mb-4">List all sources used. Ensure formatting aligns with {citationStyle} standards.</p>
              <textarea className="w-full border p-3 rounded font-mono text-sm h-64" placeholder="1. Author, A. A. (Year). Title of work..." />
            </div>

            <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
              <h5 className="font-bold text-yellow-800 text-sm">Documentation Checklist</h5>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-yellow-900">
                <label className="flex items-center"><input type="checkbox" className="mr-2"/> Data Logs</label>
                <label className="flex items-center"><input type="checkbox" className="mr-2"/> Interview Transcripts</label>
                <label className="flex items-center"><input type="checkbox" className="mr-2"/> Meeting Notes</label>
                <label className="flex items-center"><input type="checkbox" className="mr-2"/> Team Résumés</label>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: FINAL DELIVERABLES */}
        {step === 5 && (
          <div className="space-y-8 animate-fade-in text-center py-8">
            <h3 className="text-2xl font-bold text-gray-800">Final Submission & Defense</h3>
            
            <div className="grid md:grid-cols-2 gap-8 text-left max-w-4xl mx-auto">
              {/* Written Report */}
              <div className="border rounded-xl p-6 hover:shadow-md transition">
                <h4 className="font-bold text-indigo-700 mb-2">1. Written Report</h4>
                <p className="text-sm text-gray-600 mb-4">Must demonstrate advanced grammar, clarity, professional tone, and structure.</p>
                <button className="w-full border-2 border-dashed border-indigo-300 text-indigo-500 py-3 rounded hover:bg-indigo-50">
                  📄 Upload Final PDF
                </button>
              </div>

              {/* Oral Presentation */}
              <div className="border rounded-xl p-6 hover:shadow-md transition">
                <h4 className="font-bold text-indigo-700 mb-2">2. Oral Presentation</h4>
                <p className="text-sm text-gray-600 mb-4">Formal presentation to peers/instructors. Focus on communication skills.</p>
                <button className="w-full border-2 border-dashed border-indigo-300 text-indigo-500 py-3 rounded hover:bg-indigo-50">
                  🎙️ Upload Slides / Recording
                </button>
              </div>
            </div>

            <div className="mt-8">
              <button className="bg-green-600 text-white px-8 py-3 rounded shadow-lg hover:bg-green-700 font-bold">
                Submit Capstone Project
              </button>
            </div>
          </div>
        )}

      </div>

      {/* --- FOOTER NAV --- */}
      <div className="p-6 border-t bg-gray-50 flex justify-between rounded-b-xl">
        <button 
          onClick={() => setStep(s => Math.max(1, s-1))}
          disabled={step === 1}
          className="px-6 py-2 text-gray-500 hover:text-gray-900 disabled:opacity-30"
        >
          ← Previous
        </button>
        <button 
          onClick={() => setStep(s => Math.min(5, s+1))}
          disabled={step === 5}
          className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-30"
        >
          Next Phase →
        </button>
      </div>
    </div>
  );
};

export default CapstoneProject;